import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import {
  View,
  ScrollView,
  FlatList,
  TextInput,
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  type ScrollViewProps,
  type ViewProps,
  type FlatListProps,
  type TextInputProps,
  type TextInput as TextInputRef,
} from 'react-native';
import BottomSheet, {
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetView,
  BottomSheetFlatList,
  BottomSheetTextInput,
  type BottomSheetModalProps,
} from '@gorhom/bottom-sheet';
import { useTheme, SPACING, BORDER_RADIUS, ELEVATION, Z_INDEX } from '../../theme';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { useDesktopSheetStyle } from '../../hooks/useDesktopSheetStyle';
import { useMaxSheetHeight } from '../../hooks/useMaxSheetHeight';
import { useSheetPresentation } from '../../hooks/useSheetPresentation';
import { useBottomSheetTabBarVisibility } from '../../hooks/useBottomSheetTabBarVisibility';
import { useUIStore } from '../../store/uiStore';
import { SheetBackdrop, SheetBackdropLight } from './SheetBackdrop';
import { DialogPortal } from './DialogPortal';
import { useEscapeToClose } from '../../utils/escapeStack';

type SheetMode = 'sheet' | 'dialog';

const SheetModeContext = createContext<SheetMode>('sheet');

// Escape handling lives in utils/escapeStack (shared with ConfirmDialog).
export { useEscapeToClose };

interface AdaptiveSheetProps {
  /** Controlled open state — the single source of truth on both platforms. */
  isOpen: boolean;
  /** uiStore.sheetOpenTick for uiStore-driven sheets (re-fires present()). */
  tick?: number;
  /**
   * Dismiss work (clear local state, conditional closeSheet(), onClose()…).
   * Sheet mode: runs after the dismiss animation via gorhom onDismiss.
   * Dialog mode: runs when the dialog closes (Escape / scrim / isOpen flip).
   */
  onDismiss?: () => void;
  /**
   * Imperative close request from inside the dialog (scrim click / Escape).
   * Should flip whatever drives `isOpen` (e.g. uiStore.closeSheet or the
   * parent's visible-prop setter).
   */
  onRequestClose: () => void;
  /** Desktop dialog max width. Default 480. */
  dialogWidth?: number;
  /** 'dark' (default) or 'light' backdrop, matching today's per-sheet choice. */
  backdrop?: 'dark' | 'light';
  /** Forwarded to gorhom only (virtual-keyboard handling is mobile-only). */
  keyboardBehavior?: BottomSheetModalProps['keyboardBehavior'];
  keyboardBlurBehavior?: BottomSheetModalProps['keyboardBlurBehavior'];
  androidKeyboardInputMode?: BottomSheetModalProps['android_keyboardInputMode'];
  /**
   * 'modal' (default): gorhom BottomSheetModal with dynamic sizing.
   * 'inline': mounted-while-open plain BottomSheet with fixed snapPoints and
   * onChange close detection (the CommentsSheet/ClaimSheet pattern).
   */
  variant?: 'modal' | 'inline';
  /** Fixed snap points — inline variant only. */
  snapPoints?: (string | number)[];
  testID?: string;
  children: ReactNode;
}

/**
 * One sheet, two presentations (folds debt M3 — the copy-pasted modal
 * lifecycle lives here now):
 *
 * - Everywhere except the desktop shell: a gorhom BottomSheetModal with the
 *   house conventions baked in (accessible={false}, dynamic sizing capped by
 *   useMaxSheetHeight, SheetBackdrop, useDesktopSheetStyle, ref-counted
 *   tab-bar hold via useSheetPresentation).
 * - Desktop shell (web >1024px): a centered dialog over a scrim. No gorhom —
 *   plain Views, Escape + scrim-click to close. The tab-bar refcount is held
 *   symmetrically so CreatePostCoachmark suppression behaves identically.
 *
 * Children must use AdaptiveSheet.ScrollView / AdaptiveSheet.View instead of
 * the gorhom primitives — they resolve per presentation (gorhom scrollables
 * crash outside a gorhom container).
 *
 * The presentation locks when the sheet opens: resizing across the 1024px
 * boundary mid-open keeps the current presentation until dismissed, so a
 * presented gorhom modal is never stranded without its dismiss path.
 */
export function AdaptiveSheet({
  isOpen,
  tick,
  onDismiss,
  onRequestClose,
  dialogWidth = 480,
  backdrop = 'dark',
  keyboardBehavior,
  keyboardBlurBehavior,
  androidKeyboardInputMode,
  variant = 'modal',
  snapPoints,
  testID,
  children,
}: AdaptiveSheetProps) {
  const isDesktopShell = useDesktopShell();

  // Lock the presentation at open time.
  const wasOpen = useRef(false);
  const modeRef = useRef<SheetMode>(isDesktopShell ? 'dialog' : 'sheet');
  if (isOpen && !wasOpen.current) {
    modeRef.current = isDesktopShell ? 'dialog' : 'sheet';
  }
  wasOpen.current = isOpen;
  const mode = isOpen ? modeRef.current : isDesktopShell ? 'dialog' : 'sheet';

  if (mode === 'dialog') {
    return (
      <DialogPresentation
        isOpen={isOpen}
        onDismiss={onDismiss}
        onRequestClose={onRequestClose}
        dialogWidth={dialogWidth}
        testID={testID}>
        {children}
      </DialogPresentation>
    );
  }

  if (variant === 'inline') {
    return (
      <InlineSheetPresentation
        isOpen={isOpen}
        onDismiss={onDismiss}
        keyboardBehavior={keyboardBehavior}
        snapPoints={snapPoints ?? ['75%']}>
        {children}
      </InlineSheetPresentation>
    );
  }

  return (
    <SheetPresentation
      isOpen={isOpen}
      tick={tick}
      onDismiss={onDismiss}
      backdrop={backdrop}
      keyboardBehavior={keyboardBehavior}
      keyboardBlurBehavior={keyboardBlurBehavior}
      androidKeyboardInputMode={androidKeyboardInputMode}>
      {children}
    </SheetPresentation>
  );
}

function InlineSheetPresentation({
  isOpen,
  onDismiss,
  keyboardBehavior,
  snapPoints,
  children,
}: {
  isOpen: boolean;
  onDismiss?: () => void;
  keyboardBehavior?: BottomSheetModalProps['keyboardBehavior'];
  snapPoints: (string | number)[];
  children: ReactNode;
}) {
  const { colors } = useTheme();
  const sheetStyle = useDesktopSheetStyle(inlineStyles.sheet);
  useBottomSheetTabBarVisibility(isOpen);

  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const sheetRef = useRef<BottomSheet>(null);
  useEscapeToClose(isOpen, () => sheetRef.current?.close());

  const handleChange = useCallback((index: number) => {
    if (index === -1) {
      // Tab-bar release happens via useBottomSheetTabBarVisibility's cleanup
      // once the parent flips isOpen — releasing here too would double-count
      // and steal any other holder's refcount.
      onDismissRef.current?.();
    }
  }, []);

  if (!isOpen) return null;

  return (
    <BottomSheet
      ref={sheetRef}
      index={0}
      snapPoints={snapPoints}
      enablePanDownToClose
      onChange={handleChange}
      enableDynamicSizing={false}
      keyboardBehavior={keyboardBehavior}
      accessible={false}
      backgroundStyle={{ backgroundColor: colors.background }}
      handleIndicatorStyle={{ backgroundColor: colors.textTertiary }}
      style={sheetStyle}>
      <SheetModeContext.Provider value="sheet">{children}</SheetModeContext.Provider>
    </BottomSheet>
  );
}

const inlineStyles = StyleSheet.create({
  sheet: {
    zIndex: 999,
    elevation: 999,
  },
});

function SheetPresentation({
  isOpen,
  tick,
  onDismiss,
  backdrop,
  keyboardBehavior,
  keyboardBlurBehavior,
  androidKeyboardInputMode,
  children,
}: Omit<AdaptiveSheetProps, 'onRequestClose' | 'dialogWidth' | 'testID'>) {
  const { colors } = useTheme();
  const modalRef = useRef<BottomSheetModal>(null);
  const sheetStyle = useDesktopSheetStyle();
  const maxSheetHeight = useMaxSheetHeight();

  const { handleDismiss } = useSheetPresentation(modalRef, isOpen, { tick, onDismiss });

  useEscapeToClose(isOpen, () => modalRef.current?.dismiss());

  return (
    <BottomSheetModal
      ref={modalRef}
      enablePanDownToClose
      enableDynamicSizing
      maxDynamicContentSize={maxSheetHeight}
      accessible={false}
      onDismiss={handleDismiss}
      backdropComponent={backdrop === 'light' ? SheetBackdropLight : SheetBackdrop}
      backgroundStyle={{ backgroundColor: colors.surfaceElevated }}
      handleIndicatorStyle={{ backgroundColor: colors.border }}
      keyboardBehavior={keyboardBehavior}
      keyboardBlurBehavior={keyboardBlurBehavior}
      android_keyboardInputMode={androidKeyboardInputMode}
      style={sheetStyle}>
      <SheetModeContext.Provider value="sheet">{children}</SheetModeContext.Provider>
    </BottomSheetModal>
  );
}

function DialogPresentation({
  isOpen,
  onDismiss,
  onRequestClose,
  dialogWidth,
  testID,
  children,
}: Pick<AdaptiveSheetProps, 'isOpen' | 'onDismiss' | 'onRequestClose' | 'testID' | 'children'> & {
  dialogWidth: number;
}) {
  const { colors } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const maxHeight = useMaxSheetHeight();
  const hideTabBar = useUIStore((s) => s.hideTabBar);
  const showTabBar = useUIStore((s) => s.showTabBar);

  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Symmetric overlay hold + dismiss work. There is no close animation in
  // dialog mode, so the unmount effect IS the dismiss path.
  //
  // Focus goes back to whatever opened the dialog once it closes
  // (docs/plans/panel-system.md, "Escape and focus"). Without this, closing
  // left focus on <body> and a keyboard user started again from the top.
  useEffect(() => {
    if (!isOpen) return;
    hideTabBar();
    const opener =
      Platform.OS === 'web' && typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;
    return () => {
      showTabBar();
      onDismissRef.current?.();
      if (opener && opener !== document.body && opener.isConnected) opener.focus();
    };
  }, [isOpen, hideTabBar, showTabBar]);

  // Escape closes — same stack-arbitrated hook as the sheet presentations so
  // stacked overlays dismiss one at a time.
  useEscapeToClose(isOpen, onRequestClose);

  const handleScrimPress = useCallback(() => onRequestClose(), [onRequestClose]);

  if (!isOpen) return null;

  return (
    <DialogPortal>
      <View
        style={[styles.overlay, { backgroundColor: colors.overlay }]}
        testID={testID}
        // @ts-expect-error dataSet is a react-native-web prop (drives the
        // scrim fade-in keyframe in app/+html.tsx's motion CSS); not in RN's
        // type defs
        dataSet={{ rediAnim: 'dialog-scrim' }}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={handleScrimPress}
          accessible={false}
          accessibilityLabel="Close dialog"
        />
        <View
          style={[
            styles.dialog,
            ELEVATION.xl,
            {
              backgroundColor: colors.surfaceElevated,
              borderColor: colors.border,
              width: Math.min(dialogWidth, windowWidth - SPACING.xl * 2),
              maxHeight,
            },
          ]}
          // RN Web: stop scrim presses originating inside the card.
          onStartShouldSetResponder={() => true}
          // @ts-expect-error dataSet is a react-native-web prop (drives the
          // card fade+scale keyframe in app/+html.tsx's motion CSS); not in
          // RN's type defs
          dataSet={{ rediAnim: 'dialog-card' }}>
          <SheetModeContext.Provider value="dialog">{children}</SheetModeContext.Provider>
        </View>
      </View>
    </DialogPortal>
  );
}

/** Scroll container that resolves per presentation. */
function AdaptiveScrollView({ children, ...props }: ScrollViewProps) {
  const mode = useContext(SheetModeContext);
  if (mode === 'dialog') {
    return (
      <ScrollView showsVerticalScrollIndicator={false} {...props}>
        {children}
      </ScrollView>
    );
  }
  return <BottomSheetScrollView {...props}>{children}</BottomSheetScrollView>;
}

/**
 * Current presentation ('sheet' | 'dialog') — for content that needs a
 * different layout strategy per mode (e.g. fixed dialog heights for
 * flex lists).
 */
export function useSheetPresentationMode(): SheetMode {
  return useContext(SheetModeContext);
}

/** List that resolves per presentation (gorhom list inside gorhom sheets). */
function AdaptiveFlatList<T>(props: FlatListProps<T>) {
  const mode = useContext(SheetModeContext);
  if (mode === 'dialog') {
    return <FlatList {...props} />;
  }
  const GorhomList = BottomSheetFlatList as unknown as React.ComponentType<FlatListProps<T>>;
  return <GorhomList {...props} />;
}

/** Text input that resolves per presentation (gorhom input coordinates the
 *  sheet's keyboard avoidance; dialogs are desktop-only and need none). */
const AdaptiveTextInput = React.forwardRef<TextInputRef, TextInputProps>(
  function AdaptiveTextInput(props, ref) {
    const mode = useContext(SheetModeContext);
    if (mode === 'dialog') {
      return <TextInput ref={ref} {...props} />;
    }
    const GorhomInput = BottomSheetTextInput as unknown as typeof TextInput;
    return <GorhomInput ref={ref} {...props} />;
  }
);

/** Static container that resolves per presentation. */
function AdaptiveSheetView({ children, ...props }: ViewProps) {
  const mode = useContext(SheetModeContext);
  if (mode === 'dialog') {
    return <View {...props}>{children}</View>;
  }
  return <BottomSheetView {...props}>{children}</BottomSheetView>;
}

AdaptiveSheet.ScrollView = AdaptiveScrollView;
AdaptiveSheet.View = AdaptiveSheetView;
AdaptiveSheet.FlatList = AdaptiveFlatList;
AdaptiveSheet.TextInput = AdaptiveTextInput;

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: Z_INDEX.dialog,
  },
  dialog: {
    borderRadius: BORDER_RADIUS.xl,
    borderWidth: 1,
    overflow: 'hidden',
    // In sheet mode gorhom's drag handle sits above the content and supplies
    // the top gap. A dialog has no handle, so without this the first thing in
    // every sheet (a heading, in almost all of them) is flush against the top
    // edge while its own paddingHorizontal holds it clear of the sides. Lives
    // here rather than in each sheet's header: 13 of the 14 sheets set no top
    // padding of their own, so this is the presentation's job.
    paddingTop: SPACING.md,
  },
});
